import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    CheckCircle2,
    Loader2,
    ShieldCheck,
    Globe,
    Building2,
    ArrowRight,
    Sparkles,
    MessageSquare,
    Zap,
    AlertCircle,
    X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { setIsConnected } from "../../../store/whatsappSlice";
import { Badge } from "../../tailwind/badge";
import { Card } from "../../tailwind/card";
import { Button } from "../../tailwind/button";
import { cn } from "../../../lib/utils";
import { useWhatsAppSDK } from "../../../hooks/useWhatsAppSDK";

export default function WhatsAppConnect({ onClose }) {
    const navigate = useNavigate();
    const [step, setStep] = useState("intro");
    const dispatch = useDispatch();
    const phones = useSelector(state => state.whatsapp.phones);

    const postSuccess = useCallback(() => {
        setStep("verified");
    }, []);

    const { sdkLoaded, isConnecting, handleConnect: sdkHandleConnect } = useWhatsAppSDK(postSuccess);

    useEffect(() => {
        if (step === "connecting" && !isConnecting) {
            setStep("intro");
        }
    }, [isConnecting, step]);

    const handleConnect = useCallback(() => {
        setStep("connecting");
        sdkHandleConnect();
    }, [sdkHandleConnect]);

    useEffect(() => {
        if (phones && phones.length > 0) {
            dispatch(setIsConnected(true));
        }
    }, [dispatch, phones]);

    const handleContinue = () => {
        dispatch(setIsConnected(true));
        dispatch({ type: 'FETCH_WABA_IDS' });
        navigate("/whatsapp/dashboard");
        if (onClose) onClose();
    };

    const prerequisites = [
        { icon: Building2, label: "Meta Business Manager account", status: "required" },
        { icon: ShieldCheck, label: "Business verification completed", status: "required" },
        { icon: Globe, label: "Working business website", status: "recommended" },
    ];

    const steps = [
        { num: 1, title: "Connect Business", desc: "Link your Meta Business Manager account" },
        { num: 2, title: "Verify Number", desc: "We'll verify your WhatsApp Business number" },
        { num: 3, title: "Start Messaging", desc: "Begin sending messages to your customers" },
    ];

    return (
        <div className="max-w-3xl mx-auto py-8 relative">
            {/* Close Button */}
            <button
                onClick={onClose}
                className="absolute -top-2 -right-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                title="Close"
            >
                <X className="h-6 w-6" />
            </button>
            <AnimatePresence mode="wait">
                {step === "intro" && (
                    <motion.div
                        key="intro"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                    >
                        {/* Header */}
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center gap-2 bg-[hsl(158,48%,36%)]/10 text-[hsl(158,48%,32%)] px-4 py-2 rounded-full text-sm font-medium mb-6">
                                <Sparkles className="h-4 w-4" />
                                Welcome to WhatsApp Business API
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                                Connect your WhatsApp Business Account
                            </h1>
                            <p className="text-gray-500 text-lg max-w-xl mx-auto">
                                Follow these simple steps to set up your WhatsApp Business API and start messaging customers.
                            </p>
                        </div>

                        {/* Prerequisites */}
                        <Card className="p-6 mb-6 border-gray-200">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                                <ShieldCheck className="h-5 w-5 text-gray-500 dark:text-gray-100" />
                                Prerequisites
                            </h3>
                            <div className="space-y-3">
                                {prerequisites.map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800"
                                    >
                                        <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        </div>
                                        <span className="text-gray-700 dark:text-gray-100 flex-1">{item.label}</span>
                                        <Badge
                                            variant="secondary"
                                            className={cn(
                                                "text-[10px] font-medium",
                                                item.status === "required"
                                                    ? "bg-blue-50 text-blue-700"
                                                    : "bg-gray-100 text-gray-500"
                                            )}
                                        >
                                            {item.status === "required" ? "Required" : "Recommended"}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* One-Click Setup */}
                        <Card className="p-6 mb-6 border-[hsl(158,48%,36%)]/30 bg-gradient-to-br from-[hsl(158,48%,36%)]/5 to-transparent">
                            <div className="flex items-start gap-4">
                                <div className="h-12 w-12 rounded-xl bg-[hsl(158,48%,36%)] flex items-center justify-center flex-shrink-0">
                                    <Zap className="h-6 w-6 text-white" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 text-lg mb-1">One-Click Setup</h3>
                                    <p className="text-gray-600 dark:text-gray-200 text-sm mb-4">
                                        Click the button below to start your WhatsApp Business API setup. We'll guide you through the entire process automatically.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                        <span className="flex items-center gap-1.5">
                                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                                            Automatic account creation
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                                            Guided verification process
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                                            Instant phone number registration
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {!sdkLoaded && (
                                <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2 text-sm text-blue-700">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading Facebook SDK...
                                </div>
                            )}

                            <Button
                                size="lg"
                                className="w-full h-14 text-base bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold shadow-lg shadow-[hsl(158,48%,36%)]/20 mt-6 gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer"
                                onClick={handleConnect}
                                disabled={!sdkLoaded || isConnecting}
                            >
                                <MessageSquare className="h-5 w-5" />
                                Start WhatsApp Business Setup
                                <ArrowRight className="h-5 w-5 ml-1" />
                            </Button>
                        </Card>

                        {/* What Happens Next */}
                        <div className="mt-10">
                            <h3 className="font-semibold text-gray-900 mb-6 text-center">What Happens Next</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {steps.map((s, i) => (
                                    <div
                                        key={s.num}
                                        className="relative p-5 rounded-xl bg-gradient-to-br from-[hsl(217,33%,17%)] to-[hsl(217,33%,22%)] text-white"
                                    >
                                        <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-[hsl(158,48%,36%)] flex items-center justify-center text-white font-bold text-sm shadow-lg">
                                            {s.num}
                                        </div>
                                        <h4 className="font-semibold mt-2 mb-1">{s.title}</h4>
                                        <p className="text-sm text-gray-400">{s.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}

                {step === "connecting" && (
                    <motion.div
                        key="connecting"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="text-center py-20"
                    >
                        <div className="h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Connecting to Meta...</h2>
                        <p className="text-gray-500">Please complete the setup in the popup window.</p>

                        <div className="mt-8 max-w-md mx-auto">
                            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-3 text-left">
                                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-blue-700">
                                    <p className="font-medium mb-1">A popup window should have appeared</p>
                                    <p>If you don't see it, please check if your browser blocked the popup and allow it to continue.</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {step === "verified" && (
                    <motion.div
                        key="verified"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className="text-center py-12"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", delay: 0.1 }}
                            className="h-24 w-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                        >
                            <CheckCircle2 className="h-12 w-12 text-green-600" />
                        </motion.div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Your WhatsApp Business Account is Ready!
                        </h2>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto">
                            Congratulations! You can now start sending messages to your customers directly from Prodyt CRM.
                        </p>

                        <div className="inline-flex items-center gap-2 text-sm text-green-700 bg-green-50 py-2 px-4 rounded-full mb-8">
                            <ShieldCheck className="h-4 w-4" />
                            Verified Business Account
                        </div>

                        <Card className="max-w-md mx-auto p-6 mb-8 text-left">
                            <h4 className="font-semibold text-gray-900 mb-4">Account Details</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-gray-500">Phone Number</span>
                                    <span className="font-medium text-gray-900">{phones?.[0]?.number || ''}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-gray-500">Display Name</span>
                                    <span className="font-medium text-gray-900">{phones?.[0]?.label || ''}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-gray-100">
                                    <span className="text-gray-500">Messaging Tier</span>
                                    <span className="font-medium text-gray-900">{phones?.[0]?.currentLimit ?? ''}</span>
                                </div>
                            </div>
                        </Card>

                        <Button
                            size="lg"
                            className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold px-8 gap-2"
                            onClick={handleContinue}
                        >
                            Go to Dashboard
                            <ArrowRight className="h-5 w-5" />
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}